import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingCart } from 'lucide-react';
import { useCartStore } from '../store/cartStore';
import { SITE_NAME } from '../lib/brand';

const NAV_LINKS = [
  { path: '/', label: '首頁' },
  { path: '/story', label: '品牌故事' },
  // 媒體報道：內容就緒後加回（media-coverage CMS）
  { path: '/store', label: '商店' },
  { path: '/offers', label: '限時優惠' },
  { path: '/shipping', label: '運送說明' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const totalItems = useCartStore((s) => s.totalItems());

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-x1493-dark/95 backdrop-blur-md border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          {/* 正式標誌：開盒即播（客人 2026-09-09 定案：概念A開盒版） */}
          <svg width="40" height="40" viewBox="0 0 112 100" aria-hidden="true" className="flex-shrink-0">
            <g transform="translate(6,-10)">
              <rect x="24" y="36" width="56" height="22" rx="4" fill="#092d21" />
              <rect x="-8" y="51.5" width="28" height="9" rx="4.5" fill="#f3efe6" transform="rotate(45 20 56)" />
              <rect x="84" y="51.5" width="28" height="9" rx="4.5" fill="#f3efe6" transform="rotate(-45 84 56)" />
              <rect x="16" y="54" width="72" height="50" rx="10" fill="#f3efe6" />
              <circle cx="52" cy="42" r="22" fill="#d8b25c" />
              <path d="M 45 30 L 45 54 L 66 42 Z" fill="#0b3527" stroke="#0b3527" strokeWidth="5" strokeLinejoin="round" />
            </g>
          </svg>
          <div className="block">
            <span className="text-lg sm:text-xl font-bold text-white tracking-wide">{SITE_NAME}</span>
            <p className="text-[8.5px] sm:text-[10px] text-x1493-secondary tracking-[0.08em] font-bold whitespace-nowrap">真人試過先上架——香港信任直播間</p>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                isActive(link.path)
                  ? 'text-x1493-primary bg-white'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/store')}
            className="relative p-2 rounded-xl text-white hover:bg-white/10 transition"
            aria-label="購物車"
          >
            <ShoppingCart size={22} />
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 bg-x1493-primary text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </button>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-xl text-white hover:bg-white/10 transition"
            aria-label="選單"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-x1493-dark px-4 pb-4">
          <nav className="flex flex-col gap-1 pt-2">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-xl text-base font-medium transition ${
                  isActive(link.path)
                    ? 'text-x1493-primary bg-white'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
